import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MessageSquare, User, Clock, ChevronRight, Home, Briefcase, Utensils, ShoppingBag, Heart, Plus, Search } from 'lucide-react';
import apiClient from '../api/client';
import { useLanguage } from '../contexts/LanguageContext';
import usePageTitle from '../hooks/usePageTitle';

const ForumCategory = ({ icon: Icon, name, description, threads, posts, onClick, t }) => (
  <div role="button" tabIndex={0} onClick={onClick}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
    className="group p-4 border-b border-dark-border/50 last:border-0 hover:bg-dark-surface/50 transition-all duration-300 flex items-center justify-between gap-4 cursor-pointer">
    <div className="flex items-center space-x-4 flex-grow">
      <div className="bg-dark-surface p-3 rounded-xl flex-shrink-0 group-hover:bg-dark-elevated border border-dark-border transition-colors">
        <Icon className="h-5 w-5 text-accent" />
      </div>
      <div className="flex-grow min-w-0">
        <h4 className="font-semibold text-white text-sm mb-0.5">{name}</h4>
        <p className="text-xs text-gray-500 line-clamp-1">{description}</p>
      </div>
    </div>
    <div className="hidden md:flex items-center space-x-6 flex-shrink-0">
      <div className="text-center min-w-[50px]"><div className="text-sm font-bold text-white">{threads}</div><div className="text-[10px] text-gray-500">{t('forum.threads')}</div></div>
      <div className="text-center min-w-[50px]"><div className="text-sm font-bold text-white">{posts}</div><div className="text-[10px] text-gray-500">{t('forum.posts')}</div></div>
      <ChevronRight className="h-4 w-4 text-gray-600 flex-shrink-0 group-hover:text-accent transition-colors" />
    </div>
  </div>
);

const ForumPost = ({ post, onClick, t }) => {
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 60) return `${diffMins}${t('forum.minutesAgo')}`;
    if (diffHours < 24) return `${diffHours}${t('forum.hoursAgo')}`;
    if (diffDays < 7) return `${diffDays}${t('forum.daysAgo')}`;
    return date.toLocaleDateString();
  };
  return (
    <div onClick={onClick} className={`p-4 border-b border-dark-border/50 last:border-0 hover:bg-dark-surface/50 transition-all duration-300 cursor-pointer ${post.is_pinned ? 'bg-amber-500/5' : ''}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-grow min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            {post.is_pinned && <span className="px-2 py-0.5 bg-amber-500/10 text-amber-400 text-[10px] font-bold rounded-full border border-amber-500/20">{t('forum.pinned')}</span>}
            {post.is_hidden && <span className="px-2 py-0.5 bg-dark-surface text-gray-500 text-[10px] font-medium rounded-full border border-dark-border">Hidden</span>}
            <span className="px-2 py-0.5 bg-accent/10 text-accent text-[10px] font-medium rounded-full border border-accent/20">{post.category?.name || t('forum.uncategorized')}</span>
          </div>
          <h5 className="text-sm font-medium text-white hover:text-accent transition-colors line-clamp-2 mb-2">{post.title}</h5>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
            <span className="flex items-center"><User className="h-3 w-3 mr-1" />{post.author?.username || post.author?.name || t('forum.anonymous')}</span>
            <span>{t('forum.reply')} {post.reply_count || 0}</span>
            <span>{t('forum.views')} {post.views || 0}</span>
            {post.last_reply_at && <span className="flex items-center"><Clock className="h-3 w-3 mr-1" />{formatDate(post.last_reply_at)}</span>}
          </div>
        </div>
      </div>
    </div>
  );
};

const RecentPost = ({ post, onClick, t }) => {
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 60) return `${diffMins}${t('forum.minutesAgo')}`;
    if (diffHours < 24) return `${diffHours}${t('forum.hoursAgo')}`;
    if (diffDays < 7) return `${diffDays}${t('forum.daysAgo')}`;
    return date.toLocaleDateString();
  };
  return (
    <div onClick={onClick} className="py-3 border-b border-dark-border/50 last:border-0 hover:bg-dark-surface/30 transition-colors cursor-pointer px-1">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-medium text-accent bg-accent/10 px-2 py-0.5 rounded-full border border-accent/20">{post.category?.name || t('forum.uncategorized')}</span>
        <span className="text-[10px] text-gray-500 flex items-center"><Clock className="h-3 w-3 mr-1" />{formatDate(post.created_at)}</span>
      </div>
      <h5 className="text-sm font-medium text-white hover:text-accent transition-colors line-clamp-2 mb-1">{post.title}</h5>
      <div className="flex items-center"><User className="h-3 w-3 text-gray-500 mr-1" /><span className="text-xs text-gray-500">{post.author?.username || post.author?.name || t('forum.anonymous')}</span></div>
    </div>
  );
};

const BACKEND_CATEGORY_NAMES = ["房屋租售", "求职招聘", "美食推荐", "二手交易", "生活服务", "社区活动"];
const CATEGORY_ICONS = [Home, Briefcase, Utensils, ShoppingBag, MessageSquare, Heart];

const Forum = () => {
  const navigate = useNavigate();
  const { categoryId } = useParams();
  const { t, language } = useLanguage();
  usePageTitle('Forum');

  const getTranslatedCategories = () => {
    const categoriesList = t('forum.categoriesList') || [];
    return categoriesList.map((cat, index) => ({
      icon: CATEGORY_ICONS[index] || MessageSquare, name: cat.name, description: cat.description,
      threads: "0", posts: "0", backendName: BACKEND_CATEGORY_NAMES[index] || cat.name
    }));
  };

  const [categories, setCategories] = useState(getTranslatedCategories());
  const [categoryIdMap, setCategoryIdMap] = useState({});
  const [posts, setPosts] = useState([]);
  const [recentPosts, setRecentPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedCategoryName, setSelectedCategoryName] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);
  const [viewMode, setViewMode] = useState('all');
  const [hasToken, setHasToken] = useState(!!localStorage.getItem('auth_token'));

  useEffect(() => { setHasToken(!!localStorage.getItem('auth_token')); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setCategories(getTranslatedCategories()); loadCategoryMapping(); }, [language]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (categoryId) { setSelectedCategory(categoryId); const f = categories.find(c => c.id === categoryId); if (f) setSelectedCategoryName(f.backendName); } loadPosts(); }, [selectedCategory, selectedCategoryName, page, categoryId, viewMode]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (searchQuery) loadPosts(); }, [searchQuery]);

  const loadCategoryMapping = async () => {
    try {
      const backendCategories = await apiClient.getCategories();
      const map = {};
      backendCategories.forEach(cat => { map[cat.name] = cat.id; });
      setCategoryIdMap(map);
      const current = getTranslatedCategories();
      setCategories(current.map(orig => {
        const bc = backendCategories.find(b => b.name === orig.backendName);
        return bc ? { ...orig, id: bc.id, threads: formatCount(bc.post_count || 0), posts: formatCount(bc.reply_count || 0) } : orig;
      }));
    } catch (error) { console.error('Error loading category mapping:', error); }
  };

  const loadPosts = async () => {
    setLoading(true);
    try {
      let data;
      if (viewMode === 'my') { data = await apiClient.getMyPosts({ page, limit: 20 }); }
      else {
        const params = { page, limit: 20 };
        let cid = categoryId;
        if (selectedCategoryName && categoryIdMap[selectedCategoryName]) cid = categoryIdMap[selectedCategoryName];
        else if (selectedCategory) cid = selectedCategory;
        if (cid) params.category_id = cid;
        if (searchQuery) params.search = searchQuery;
        data = await apiClient.getPosts(params);
      }
      setPosts(data.posts || []);
      setTotalPosts(data.total || 0);
      const catId = selectedCategory || categoryId;
      if (viewMode === 'all' && !catId && !searchQuery) {
        try { const r = await apiClient.getPosts({ page: 1, limit: 5 }); setRecentPosts(r.posts || []); } catch (err) { console.error(err); }
      } else setRecentPosts([]);
    } catch (error) { console.error(error); setPosts([]); setRecentPosts([]); }
    finally { setLoading(false); }
  };

  const handleCategoryClick = (category) => {
    const cid = category.id || categoryIdMap[category.backendName];
    if (cid) { setSelectedCategory(cid); setSelectedCategoryName(category.backendName); setPage(1); setSearchQuery(''); navigate(`/forum/category/${cid}`); }
    else { setSelectedCategoryName(category.backendName); setSelectedCategory(null); setPage(1); setSearchQuery(''); navigate('/forum'); }
  };
  const handlePostClick = (post) => navigate(`/forum/post/${post.id}`);
  const handleSearch = (e) => { e.preventDefault(); setPage(1); setSelectedCategory(null); setSelectedCategoryName(null); navigate('/forum'); };
  const handleClearSearch = () => { setSearchQuery(''); setPage(1); setSelectedCategory(null); setSelectedCategoryName(null); navigate('/forum'); };
  const formatCount = (count) => { if (typeof count === 'string') return count; return count >= 1000 ? `${(count / 1000).toFixed(1)}k` : count.toString(); };
  const announcements = [t('forum.announcements.0'), t('forum.announcements.1'), t('forum.announcements.2')];

  return (
    <div className="-mt-16">
      {/* Hero */}
      <section className="relative pt-36 pb-10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-mesh opacity-50" />
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-gradient-to-br from-emerald-600/12 via-teal-600/8 to-transparent rounded-full blur-[100px]" />
          <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] bg-gradient-to-tr from-blue-600/8 to-transparent rounded-full blur-[100px]" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-gradient-hero mb-2">{t('forum.title')}</h1>
              <p className="text-sm text-gray-400">{t('forum.subtitle')}</p>
            </div>
            <div className="flex flex-shrink-0 gap-2 flex-wrap sm:flex-nowrap">
              {hasToken && (
                <button onClick={() => { setViewMode(v => v === 'my' ? 'all' : 'my'); setPage(1); setSelectedCategory(null); setSelectedCategoryName(null); navigate('/forum'); }}
                  className={`px-4 py-2 rounded-full text-[13px] font-medium flex items-center gap-2 transition-all duration-300 ${viewMode === 'my' ? 'bg-accent/15 text-accent border border-accent/25' : 'border border-dark-border text-gray-400 hover:text-white hover:border-dark-border-light'}`}>
                  <User className="h-4 w-4" />{language === 'zh' ? '我的帖子' : 'My Posts'}
                </button>
              )}
              <button onClick={() => { if (hasToken) navigate('/forum/create'); else navigate('/login?redirect=/forum/create'); }}
                className="px-5 py-2 bg-white text-dark rounded-full font-semibold text-[13px] hover:shadow-glow-white transition-all duration-300 flex items-center gap-2 whitespace-nowrap">
                <Plus className="h-4 w-4" />{t('forum.createPost')}
              </button>
            </div>
          </div>
          <form onSubmit={handleSearch} className="mt-6 flex flex-col sm:flex-row gap-2">
            <div className="flex-1 relative group">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><Search className="h-5 w-5 text-gray-500 group-focus-within:text-accent transition-colors" /></div>
              <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder={t('forum.searchPlaceholder')}
                className="block w-full pl-10 pr-4 py-2.5 bg-dark-card border border-dark-border rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-accent/30 focus:border-accent/50 outline-none transition-all duration-300" />
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button type="submit" className="flex-1 sm:flex-none px-6 py-2 bg-dark-surface border border-dark-border text-gray-300 rounded-xl hover:bg-dark-elevated hover:border-dark-border-light transition-all duration-300 text-sm">{t('common.search')}</button>
              {searchQuery && <button type="button" onClick={handleClearSearch} className="px-4 py-2 border border-dark-border text-gray-400 rounded-xl hover:bg-dark-surface transition-all duration-300 text-sm">{t('common.clear')}</button>}
            </div>
          </form>
        </div>
      </section>

      {/* Content */}
      <section className="relative py-8 md:py-12 min-h-[60vh]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {!searchQuery && (
            <div className="mb-8">
              <div className="bg-dark-card rounded-2xl border border-dark-border overflow-hidden shadow-dark-card">
                <div className="px-6 py-4 border-b border-dark-border/50 flex items-center justify-between">
                  <h2 className="text-[11px] font-semibold text-gray-400 uppercase tracking-[0.15em]">{t('forum.categories')}</h2>
                  {(selectedCategory || selectedCategoryName) && (
                    <button onClick={() => { setSelectedCategory(null); setSelectedCategoryName(null); setPage(1); navigate('/forum'); }} className="text-xs text-accent hover:text-accent-hover transition-colors">{t('forum.showAll')}</button>
                  )}
                </div>
                {categories.map((cat) => <ForumCategory key={cat.backendName || cat.id || cat.name} icon={cat.icon || MessageSquare} name={cat.name} description={cat.description || ''} threads={cat.threads || '0'} posts={cat.posts || '0'} onClick={() => handleCategoryClick(cat)} t={t} />)}
              </div>
            </div>
          )}

          <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
            <div className="flex-grow min-w-0 lg:min-w-[500px] lg:w-2/3">
              <div className="bg-dark-card rounded-2xl border border-dark-border overflow-hidden shadow-dark-card">
                <div className="px-6 py-4 border-b border-dark-border/50">
                  <h2 className="text-[11px] font-semibold text-gray-400 uppercase tracking-[0.15em]">
                    {searchQuery ? t('forum.searchResults') : (selectedCategory || categoryId || selectedCategoryName) ? t('forum.categoryPosts') : t('forum.recentPosts')}
                  </h2>
                </div>
                {loading ? <div className="p-8 text-center text-gray-500">{t('forum.loading')}</div>
                  : posts.length === 0 ? <div className="p-8 text-center text-gray-500">{searchQuery ? t('forum.noSearchResults') : t('forum.noPosts')}</div>
                  : <>
                    {posts.map((post) => <ForumPost key={post.id} post={post} onClick={() => handlePostClick(post)} t={t} />)}
                    {totalPosts > 20 && (
                      <div className="px-6 py-4 border-t border-dark-border/50 flex items-center justify-between">
                        <div className="text-xs text-gray-500">{t('forum.totalPosts', { count: totalPosts })}</div>
                        <div className="flex gap-2">
                          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1 border border-dark-border rounded-lg text-xs text-gray-400 disabled:opacity-30 hover:bg-dark-surface hover:border-dark-border-light transition-all">{t('common.previous')}</button>
                          <span className="px-3 py-1 text-xs text-gray-500">{t('common.page', { num: page })}</span>
                          <button onClick={() => setPage(p => p + 1)} disabled={page * 20 >= totalPosts} className="px-3 py-1 border border-dark-border rounded-lg text-xs text-gray-400 disabled:opacity-30 hover:bg-dark-surface hover:border-dark-border-light transition-all">{t('common.next')}</button>
                        </div>
                      </div>
                    )}
                  </>
                }
              </div>
            </div>

            {!searchQuery && !selectedCategory && !categoryId && !selectedCategoryName && (
              <div className="lg:w-1/3 space-y-6">
                <div className="bg-dark-card rounded-2xl border border-dark-border overflow-hidden shadow-dark-card">
                  <div className="px-6 py-4 border-b border-dark-border/50"><h3 className="text-[11px] font-semibold text-gray-400 uppercase tracking-[0.15em]">{t('forum.announcementsTitle')}</h3></div>
                  <div className="p-5">
                    <ul className="space-y-3">{announcements.map((a, i) => <li key={i} className="flex items-start"><span className="text-accent mr-2 mt-0.5 text-xs">•</span><p className="text-sm text-gray-400">{a}</p></li>)}</ul>
                  </div>
                </div>
                <div className="bg-dark-card rounded-2xl border border-dark-border overflow-hidden shadow-dark-card">
                  <div className="px-6 py-4 border-b border-dark-border/50"><h3 className="text-[11px] font-semibold text-gray-400 uppercase tracking-[0.15em]">{t('forum.recentPosts')}</h3></div>
                  <div className="p-4">
                    {recentPosts.length === 0 ? <div className="py-4 text-center text-xs text-gray-500">{t('forum.noRecentPosts')}</div>
                      : recentPosts.map((post) => <RecentPost key={post.id} post={post} onClick={() => handlePostClick(post)} t={t} />)}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Forum;
